import { Injectable, inject } from '@angular/core';
import { switchMap, tap } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, iif, Observable, of } from 'rxjs';
import { ServiceResult } from '@shared/helper/service-result';
import { TServiceResult } from '@shared/helper/t-service-result';
import { LocalRepository } from '@shared/helper/local-repository';
import { HttpBaseService } from '../base_services/http_base.service';
import { PageList } from '@shared/model/categoryAndSearch/pageList-model';
import { CustomerModel, searchedUser } from '@shared/model/customerModels/customer-model';
import { TermFilterModel } from '@shared/model/categoryAndSearch/term-filter-model';
import { SearchFilterModel } from '@shared/model/categoryAndSearch/search-filter-model';
import { IUserDataDto } from '@shared/model/user-context/user-dto.model';
import { CLIENT_DATA } from 'constants/storage-keys';
@Injectable({
    providedIn: 'root',
})

export class CustomersService extends HttpBaseService {

    customerImage_$ = new BehaviorSubject<string>('');
    customerImage_OBV = this.customerImage_$.asObservable();

    constructor() {
        const repo = inject(LocalRepository);
        const http = inject(HttpClient);

        super(repo, http, 'cu');
    }

    getCustomer(): Observable<TServiceResult<CustomerModel>> {
        return this.http.get<TServiceResult<CustomerModel>>(`${this.apiUrl}/Get`)
            .pipe(
                switchMap(res => {
                    if (res.hasError)
                        return of(res)
                    else {
                        const customer: CustomerModel = res.result;
                        return of(new TServiceResult(customer, res.message, res.error, res.hasError, res.refrenceId));
                    }
                })
            )
    }


    getUserData(): Observable<TServiceResult<IUserDataDto>> {
        const userRawData = this.Repository.getLocalItem(CLIENT_DATA);
        const userCachedData = userRawData ? JSON.parse(userRawData) : null;
        return iif(() => !!userCachedData, of(userCachedData),
            this.http.get<TServiceResult<IUserDataDto>>(`${this.apiUrl}/Get`).pipe(
                tap(res => {
                    if (!res.hasError)
                        this.Repository.setLocalItem(CLIENT_DATA, JSON.stringify(res));
                }))
        );
    }


    getGuestCustomerById(id: string): Observable<TServiceResult<CustomerModel>> {
        return this.http.get<TServiceResult<CustomerModel>>(`${this.apiUrl}/GetGuest/${id}`)
            .pipe(
                switchMap(res => {
                    if (res.hasError)
                        return of(res)
                    else {
                        const customer: CustomerModel = res.result;
                        return of(new TServiceResult(customer, res.message, res.error, res.hasError, res.refrenceId));
                    }
                })
            )
    }

    getGuestCustomerByUsername(userName: string): Observable<TServiceResult<CustomerModel>> {
        return this.http.get<TServiceResult<CustomerModel>>(`${this.apiUrl}/GetGuestByUserName/${userName}`)
            .pipe(
                switchMap(res => {
                    if (res.hasError)
                        return of(res)
                    else {
                        const customer: CustomerModel = res.result;
                        return of(new TServiceResult(customer, res.message, res.error, res.hasError, res.refrenceId));
                    }
                })
            )
    }

    getGuestCustomerUsername(userId: string): Observable<TServiceResult<string>> {
        return this.http.get<TServiceResult<string>>(`${this.apiUrl}/GetGuestUserNameById/${userId}`);
    }

    getAllCustomers(): Observable<TServiceResult<CustomerModel>> {
        return this.http.get<TServiceResult<CustomerModel>>(`${this.apiUrl}/GetAll`);
    }

    createNewCustomer(customer: CustomerModel): Observable<ServiceResult> {
        return this.http.post<ServiceResult>(`${this.apiUrl}/Create`, customer);
    }

    updateCustomer(customer: CustomerModel): Observable<ServiceResult> {
        return this.http.put<ServiceResult>(`${this.apiUrl}/UpdateProfile`, customer);
    }

    deleteCustomer(id: number): Observable<ServiceResult> {
        return this.http.delete<ServiceResult>(`${this.apiUrl}/Delete/${id}`);
    }

    getCustomerByFilter(st: SearchFilterModel): Observable<TServiceResult<PageList<CustomerModel>>> {
        return this.http.post<TServiceResult<PageList<CustomerModel>>>(`${this.apiUrl}/GetByFilter`, st);
    }

    suggestionAll(termFilter: TermFilterModel): Observable<TServiceResult<PageList<CustomerModel>>> {
        return this.http.post<TServiceResult<PageList<CustomerModel>>>(`${this.apiUrl}/SuggestionAll`, termFilter)
    }

    getInviteCode(): Observable<TServiceResult<string>> {
        return this.http.get<TServiceResult<string>>(`${this.apiUrl}/GetInviteCode`).pipe(
            tap(res => {
                if (!res.hasError) {
                    this.Repository.setLocalItem('UserInviteCode', res.result);
                }
            })
        );
    }

    updateUsername(uniqeName: string): Observable<TServiceResult<string>> {
        return this.http.put<TServiceResult<string>>(`${this.apiUrl}/UpdateUsername`, { uniqeName });
    }

    getInvisibleCustomer(st: SearchFilterModel): Observable<TServiceResult<PageList<searchedUser>>> {
        return this.http.post<TServiceResult<PageList<searchedUser>>>(`${this.apiUrl}/GetByFilterWithInvisible`, st);
    }
}

